/**
 * Talent-**integrated** gear — the RAW-faithful other half of `talentGear.ts`'s
 * uniform equip gate. An `integrated` item on a talent rank (as opposed to
 * `special_equipment`) is never manually equipped: it auto-appears on the mech,
 * slot/SP-exempt, the moment the pilot holds the rank, and vanishes the moment
 * the rank drops.
 *
 * **Pure spawn-time derivation, no storage, no command.** Both functions
 * here are pure reads over `pilot.talents` + the compendium — `set_talent_rank`
 * never mutates a mech, and nothing here does either. `talentIntegratedGrants`
 * is the pilot-specific "what auto-arms" query the spawn freeze calls;
 * `isIntegratedTalentItem` is the pilot-**independent** "is this item ever
 * manually equippable" predicate the reducer wall + editor picker call.
 *
 * **Exclusive-replace, NOT cumulative — the single easiest thing to get
 * wrong here.** A talent rank's items don't stack the way `freezePilotEffectIds`
 * (`@lancer-vtt/rules`'s `spawn.ts:277`) freezes cumulative effect ranks 1..N —
 * that idiom is for talent *effects*, which DO stack. Integrated *gear*
 * replaces: an Engineer-r3 pilot is armed with Prototype Weapon III ONLY, never
 * Prototype I + II + III. This follows COMP/CON's `PilotTalent.UnlockedRanks`
 * (`reference/compcon/src/classes/pilot/components/talent/PilotTalent.ts:19`):
 * walking ranks 1..R, a rank i is skipped when `ranks[i].exclusive && i < R` —
 * i.e. a lower EXCLUSIVE rank is superseded by a higher one the pilot also
 * holds, but a non-exclusive rank's grant always survives alongside later
 * ranks. All three Engineer ranks and all three Walking Armory ranks are
 * `exclusive: true` (so only the top rank's item grants); Nuclear Cavalier's
 * r3 (Fuel Rod Gun) is `exclusive: false`, but since it's the only integrated
 * rank on that talent the distinction is moot there — it always grants once
 * r3 is held.
 *
 * **`special_equipment` is deliberately never read here** — that's the
 * Technophile NHP carve-out (`ms_technophile_1/2/3`), which stays manually
 * equippable under the parent's uniform gate untouched. Only a rank's
 * `integrated` array feeds a grant or the manual-equip block.
 */
import type { PilotData } from "./types";
import { talentRequirement, type TalentGatedItem } from "./talentGear";

/**
 * The subset of a compendium `talent` entry this module reads: each rank's
 * `integrated` id list (the auto-grant source) and `exclusive` flag (the
 * supersede rule). `special_equipment` is intentionally NOT modeled here —
 * this module never reads it (see the module doc's NHP carve-out).
 */
export type RawTalentRank = { integrated?: unknown; exclusive?: unknown };
export type RawTalent = { ranks: RawTalentRank[] };

/**
 * The read-only compendium slice this module needs: resolve a talent by id
 * (to walk its ranks), and resolve a weapon/system by id (to split a grant's
 * ids by kind and to skip an unresolved id). Structurally typed — mirrors
 * `sp.ts`'s `SpCompendium` / `composedMounts.ts`'s `CoreBonusCompendium` —
 * so `@lancer-vtt/domain` stays free of a hard `@lancer-vtt/compendium` value
 * import; the real `Compendium` class satisfies this shape.
 */
export type TalentIntegratedCompendium = {
  getById(type: "talent", id: string): RawTalent | undefined;
  getById(type: "weapon" | "system", id: string): { id: string } | undefined;
};

/** A pilot's derived talent-integrated grant, split by kind for the two freeze seams that consume it. */
export type TalentIntegratedGrant = { weaponIds: string[]; systemIds: string[] };

/** Read a rank's `integrated` array as a clean `string[]` (defensive against a malformed/missing field). */
function integratedIdsOf(rank: RawTalentRank | undefined): string[] {
  return Array.isArray(rank?.integrated) ? rank.integrated.filter((id): id is string => typeof id === "string") : [];
}

/**
 * The auto-armed integrated-gear ids for a pilot, split into `weaponIds`/
 * `systemIds` — the pilot-specific query the spawn freeze threads into
 * `freezeWeapons`/`freezeActionIds`/`freezeActionCaps`/`freezeSystems`
 * (`@lancer-vtt/rules`'s `spawn/`).
 *
 * For each `[talentId, rank]` the pilot holds (`pilot.talents`), walks ranks
 * `1..rank`, applying the exclusive-skip (a rank `i < rank` that is
 * `exclusive` contributes nothing — it's superseded), and collects the
 * surviving ranks' `integrated` ids. Each collected id is then resolved
 * against the compendium to route it to `weaponIds` or `systemIds`; an id
 * that resolves as neither (an unloaded pack, or a malformed talent) is
 * skipped — an honest no-op, matching every other freeze seam's degrade
 * convention (`freezeWeapons`'s unresolved-id skip, `spawn.ts:160`).
 *
 * An unresolved `talentId` (the pilot holds a talent from a pack this
 * compendium didn't load) contributes nothing for that talent — also an
 * honest no-op, not a crash. A talentless pilot returns `{ weaponIds: [],
 * systemIds: [] }`.
 */
export function talentIntegratedGrants(pilot: PilotData, compendium: TalentIntegratedCompendium): TalentIntegratedGrant {
  const grantedIds: string[] = [];
  for (const [talentId, rank] of Object.entries(pilot.talents)) {
    const talent = compendium.getById("talent", talentId);
    if (!talent) continue;
    for (let r = 1; r <= rank; r++) {
      const rankData = talent.ranks[r - 1];
      if (!rankData) continue;
      if (rankData.exclusive === true && r < rank) continue; // superseded by a higher exclusive rank the pilot also holds
      grantedIds.push(...integratedIdsOf(rankData));
    }
  }
  const weaponIds: string[] = [];
  const systemIds: string[] = [];
  for (const id of grantedIds) {
    if (compendium.getById("weapon", id)) weaponIds.push(id);
    else if (compendium.getById("system", id)) systemIds.push(id);
    // else unresolved (neither a weapon nor a system in this compendium) — honest no-op
  }
  return { weaponIds, systemIds };
}

/**
 * The pilot-**independent** predicate: is this compendium item ever
 * manually equippable, or is it exclusively auto-granted by a talent's
 * `integrated[]` list? The reducer wall (`applyEquipWeapon`/`applyEquipSystem`)
 * and the editor picker both consult this to hard-block/hide integrated items
 * — never `special_equipment` (NHPs), which stay manually equippable under
 * the parent `talentGear.ts` gate untouched.
 *
 * The item can't self-identify integrated-vs-special_equipment (both carry
 * only `talent_id`/`talent_rank`) — so this keys off the **talent** side:
 * resolve the item's own `talent_id`/`talent_rank` (via `talentGear.ts`'s
 * `talentRequirement`, the same reader the parent gate uses), look up that
 * exact rank on the talent, and check whether the rank's `integrated` array
 * contains the item's own id. Pilot-independent — needs only the item's own
 * talent link, no talent enumeration, no pilot in scope.
 *
 * `false` for an item with no `talent_id` (ungated, not integrated), an
 * unresolved talent, or a rank whose `integrated` array doesn't name this
 * item (e.g. a `special_equipment`-only rank like Technophile's).
 */
export function isIntegratedTalentItem(
  item: TalentGatedItem & { id: string },
  compendium: TalentIntegratedCompendium,
): boolean {
  const req = talentRequirement(item);
  if (!req) return false;
  const talent = compendium.getById("talent", req.id);
  const rankData = talent?.ranks[req.rank - 1];
  return integratedIdsOf(rankData).includes(item.id);
}
